import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminFetch } from "./adminApi";

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/80">
      {children}
    </span>
  );
}

const STATUSES = ["NEW", "UNLOCKED", "CHAT_ACTIVE", "TRIAL_DONE", "BECAME_CLIENT", "CLOSED"];
const FORCE_ALLOWED = ["NEW", "CHAT_ACTIVE", "TRIAL_DONE", "BECAME_CLIENT", "CLOSED"];

export default function AdminRequests() {
  const params = useParams();
  const requestIdFromUrl = params?.id || null;

  const [q, setQ] = useState(requestIdFromUrl || "");
  const [status, setStatus] = useState("ALL");
  const [tutorId, setTutorId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Force status UI
  const [forceStatus, setForceStatus] = useState("CLOSED");
  const [forceReason, setForceReason] = useState("");
  const [forceBusy, setForceBusy] = useState(false);

  const pageSize = 20;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (q.trim()) p.set("q", q.trim());
    if (status !== "ALL") p.set("status", status);
    if (tutorId.trim()) p.set("tutorId", tutorId.trim());
    if (studentId.trim()) p.set("studentId", studentId.trim());
    return p.toString();
  }, [page, q, status, tutorId, studentId]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/requests?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load requests");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function openDetails(id) {
    setDetailLoading(true);
    try {
      const r = await adminFetch(`/api/admin/requests/${id}`);
      setSelected(r);

      // defaults for force panel
      setForceStatus(
        FORCE_ALLOWED.includes(r?.status) ? r.status : "CLOSED"
      );
      setForceReason("");
    } catch (e) {
      alert(e?.message || "Failed to load request details");
    } finally {
      setDetailLoading(false);
    }
  }

  async function applyForceStatus() {
    if (!selected?.id) return;
    const reason = String(forceReason || "").trim();
    if (!reason) return alert("Reason is required");

    setForceBusy(true);
    try {
      const res = await adminFetch(`/api/admin/requests/${selected.id}/status`, {
        method: "PATCH",
        body: { status: forceStatus, reason },
      });

      // refresh details + list
      const updated = res?.request || null;
      if (updated) setSelected((s) => ({ ...(s || {}), ...updated }));
      await load();
      await openDetails(selected.id);
    } catch (e) {
      alert(e?.data?.error || e?.message || "Failed to update status");
    } finally {
      setForceBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (requestIdFromUrl) {
      setQ(requestIdFromUrl);
      setStatus("ALL");
      setTutorId("");
      setStudentId("");
      setPage(1);
      openDetails(requestIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestIdFromUrl]);

  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  return (
    <div className="text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-unbounded text-2xl font-bold">Requests</h2>
          <p className="mt-2 text-white/70">Filters: status, tutorId, studentId, requestId.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="RequestId contains…"
            className="w-full md:w-56 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          >
            <option value="ALL">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <input
            value={tutorId}
            onChange={(e) => {
              setTutorId(e.target.value);
              setPage(1);
            }}
            placeholder="tutorId…"
            className="w-full md:w-44 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <input
            value={studentId}
            onChange={(e) => {
              setStudentId(e.target.value);
              setPage(1);
            }}
            placeholder="studentId…"
            className="w-full md:w-44 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <button
            onClick={load}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        {/* LIST */}
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm text-white/70">{loading ? "Loading…" : `Total: ${total}`}</div>
            <div className="flex items-center gap-2">
              <button
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
              >
                Prev
              </button>
              <Chip>Page {page}</Chip>
              <button
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
              >
                Next
              </button>
            </div>
          </div>

          {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-white/60">
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3 text-left">Id</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Tutor</th>
                  <th className="px-4 py-3 text-left">Student</th>
                  <th className="px-4 py-3 text-left">Price</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-white/60">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  items.map((r) => (
                    <tr key={r.id} className="border-b border-white/10">
                      <td className="px-4 py-3 text-white/80">
                        <div className="font-medium">{r.id}</div>
                        <div className="text-xs text-white/50">{new Date(r.createdAt).toLocaleString()}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Chip>{r.status}</Chip>
                      </td>
                      <td className="px-4 py-3 text-white/70">{r.tutorId}</td>
                      <td className="px-4 py-3 text-white/70">{r.studentId}</td>
                      <td className="px-4 py-3 text-white/70">{r.priceCredits}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openDetails(r.id)}
                            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15 ring-1 ring-white/10"
                          >
                            Details
                          </button>
                          <Link
                            to={`/admin/requests/${r.id}`}
                            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15 ring-1 ring-white/10"
                          >
                            Open
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* DETAILS + FORCE */}
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-unbounded text-lg font-bold">Details</h3>
            {detailLoading && <span className="text-xs text-white/60">Loading…</span>}
          </div>

          {!selected ? (
            <div className="mt-3 text-sm text-white/70">Select a request to view details.</div>
          ) : (
            <div className="mt-3 space-y-4 text-sm">
              <div>
                <div className="text-white/50 text-xs">Request</div>
                <div className="text-white/90 break-all">{selected.id}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-white/50 text-xs">Status</div>
                  <Chip>{selected.status}</Chip>
                </div>
                <div>
                  <div className="text-white/50 text-xs">Kind</div>
                  <Chip>{selected.kind}</Chip>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-white/50 text-xs">Tutor</div>
                  <div className="text-white/80 break-all">{selected.tutorId}</div>
                  {selected.tutor?.email && <div className="text-xs text-white/60">{selected.tutor.email}</div>}
                </div>
                <div>
                  <div className="text-white/50 text-xs">Student</div>
                  <div className="text-white/80 break-all">{selected.studentId}</div>
                  {selected.student?.email && <div className="text-xs text-white/60">{selected.student.email}</div>}
                </div>
              </div>

              <div>
                <div className="text-white/50 text-xs">Message</div>
                <div className="text-white/80 whitespace-pre-wrap">{selected.message || "—"}</div>
              </div>

              {/* FORCE STATUS */}
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3">
                <div className="font-semibold text-white/90">Force status</div>
                <div className="mt-2 grid gap-2">
                  <select
                    value={forceStatus}
                    onChange={(e) => setForceStatus(e.target.value)}
                    className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                  >
                    {FORCE_ALLOWED.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <input
                    value={forceReason}
                    onChange={(e) => setForceReason(e.target.value)}
                    placeholder="Reason (required)…"
                    className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                  />

                  <button
                    disabled={forceBusy}
                    onClick={applyForceStatus}
                    className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10 disabled:opacity-50"
                  >
                    {forceBusy ? "Applying…" : "Apply"}
                  </button>

                  <div className="text-xs text-white/50">
                    Writes to AdminAuditLog (REQUEST_FORCE_STATUS).
                  </div>
                </div>
              </div>

              <div>
                <div className="text-white/50 text-xs">Transactions</div>
                <div className="mt-2 space-y-2">
                  {(selected.transactions || []).length === 0 ? (
                    <div className="text-white/60">No transactions</div>
                  ) : (
                    selected.transactions.map((tx) => (
                      <div key={tx.id} className="rounded-lg bg-white/10 p-2">
                        <div className="flex items-center justify-between">
                          <Chip>{tx.type}</Chip>
                          <div className="text-white/80">{tx.amount}</div>
                        </div>
                        <div className="text-xs text-white/50 mt-1">
                          {new Date(tx.createdAt).toLocaleString()} · requestId: {tx.requestId || "—"}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
